"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isoWeekInputSchema = exports.isoWeekObjectSchema = exports.IsoWeekError = exports.MAX_HORIZON_WEEKS = exports.MAX_HORIZON_YEARS = exports.DEFAULT_HORIZON = exports.FACHLICHE_ZEITZONE = void 0;
exports.weeksInIsoYear = weeksInIsoYear;
exports.isValidIsoWeek = isValidIsoWeek;
exports.calendarDateIn = calendarDateIn;
exports.isoWeekOf = isoWeekOf;
exports.currentIsoWeek = currentIsoWeek;
exports.mondayOf = mondayOf;
exports.addWeeks = addWeeks;
exports.weeksBetween = weeksBetween;
exports.compareIsoWeeks = compareIsoWeeks;
exports.formatIsoWeekKey = formatIsoWeekKey;
exports.parseIsoWeek = parseIsoWeek;
exports.toPlanningWeek = toPlanningWeek;
exports.planningHorizon = planningHorizon;
const zod_1 = require("zod");
/** Fachliche Zeitzone als Vorgabe; die Funktionen nehmen sie als Parameter (E6). */
exports.FACHLICHE_ZEITZONE = 'Europe/Berlin';
exports.DEFAULT_HORIZON = { years: 4 };
exports.MAX_HORIZON_YEARS = 6;
/** Die größte Wochenzahl in sechs aufeinanderfolgenden ISO-Jahren (E5). */
exports.MAX_HORIZON_WEEKS = 314;
class IsoWeekError extends Error {
    code;
    name = 'IsoWeekError';
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
exports.IsoWeekError = IsoWeekError;
const MIN_YEAR = 1;
const MAX_YEAR = 9999;
const MS_PER_DAY = 86_400_000;
/** 1970-01-05 war ein Montag; Bezugspunkt für die fortlaufende Wochennummer. */
const REFERENCE_MONDAY = 4;
function isYearInRange(year) {
    return Number.isInteger(year) && year >= MIN_YEAR && year <= MAX_YEAR;
}
/** Tage seit 1970-01-01. `setUTCFullYear`, weil `Date.UTC` Jahre unter 100 verschiebt. */
function toDayNumber(year, month, day) {
    const date = new Date(0);
    date.setUTCFullYear(year, month - 1, day);
    return Math.round(date.getTime() / MS_PER_DAY);
}
function fromDayNumber(dayNumber) {
    const date = new Date(dayNumber * MS_PER_DAY);
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}
/** ISO-Wochentag: 1 = Montag … 7 = Sonntag. Tag 0 (1970-01-01) war ein Donnerstag. */
function isoWeekday(dayNumber) {
    return ((((dayNumber + 3) % 7) + 7) % 7) + 1;
}
function formatDayNumber(dayNumber) {
    const { year, month, day } = fromDayNumber(dayNumber);
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function parseCalendarDate(date) {
    const match = typeof date === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(date) : null;
    if (!match)
        throw new IsoWeekError('INVALID_DATE', `Kein Kalenderdatum 'YYYY-MM-DD': ${date}`);
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const dayNumber = toDayNumber(year, month, day);
    const roundTrip = fromDayNumber(dayNumber);
    if (!isYearInRange(year) ||
        roundTrip.year !== year ||
        roundTrip.month !== month ||
        roundTrip.day !== day) {
        throw new IsoWeekError('INVALID_DATE', `Kein gültiges Kalenderdatum: ${date}`);
    }
    return dayNumber;
}
/** Montag der KW 1: die Woche, die den 4. Januar enthält. */
function mondayOfWeekOne(isoYear) {
    const januaryFourth = toDayNumber(isoYear, 1, 4);
    return januaryFourth - isoWeekday(januaryFourth) + 1;
}
/** Die Woche eines Tages ist die Woche ihres Donnerstags. */
function weekOfDayNumber(dayNumber) {
    const thursday = dayNumber - isoWeekday(dayNumber) + 4;
    const isoYear = fromDayNumber(thursday).year;
    if (!isYearInRange(isoYear)) {
        throw new IsoWeekError('OUT_OF_RANGE', `Woche außerhalb 0001-W01 … 9999-W52`);
    }
    return { isoYear, isoWeek: Math.floor((thursday - mondayOfWeekOne(isoYear)) / 7) + 1 };
}
function assertValidWeek(week) {
    if (!isValidIsoWeek(week)) {
        throw new IsoWeekError('INVALID_WEEK', `Keine gültige ISO-Woche: ${String(week?.isoYear)}-W${String(week?.isoWeek)}`);
    }
}
function mondayDayNumber(week) {
    assertValidWeek(week);
    return mondayOfWeekOne(week.isoYear) + (week.isoWeek - 1) * 7;
}
/** Fortlaufende Wochennummer; Grundlage für Arithmetik und Vergleich. */
function weekIndex(week) {
    return (mondayDayNumber(week) - REFERENCE_MONDAY) / 7;
}
/** Ein ISO-Jahr hat 53 Wochen, wenn es mit Donnerstag beginnt, im Schaltjahr auch mit Mittwoch. */
function weeksInIsoYear(isoYear) {
    if (!isYearInRange(isoYear)) {
        throw new IsoWeekError('INVALID_WEEK', `ISO-Jahr außerhalb 1 … 9999: ${String(isoYear)}`);
    }
    const weekday = isoWeekday(toDayNumber(isoYear, 1, 1));
    const leapYear = isoYear % 4 === 0 && (isoYear % 100 !== 0 || isoYear % 400 === 0);
    return weekday === 4 || (weekday === 3 && leapYear) ? 53 : 52;
}
/** Prüft, ohne zu werfen. Signatur unverändert seit AC-015 (vormals in common.ts). */
function isValidIsoWeek({ isoYear, isoWeek }) {
    if (!isYearInRange(isoYear))
        return false;
    if (!Number.isInteger(isoWeek) || isoWeek < 1)
        return false;
    return isoWeek <= weeksInIsoYear(isoYear);
}
/** Einzige Stelle Zeitpunkt → Kalenderdatum (E6). */
function calendarDateIn(instant, timeZone) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw new IsoWeekError('INVALID_DATE', 'Kein gültiger Zeitpunkt');
    }
    // Ohne Zeitzone fiele Intl still auf die des Prozesses zurück; reine
    // Versätze wie '+02:00' kennen keine Sommerzeit. Beides ist hier ein Fehler.
    if (typeof timeZone !== 'string' || timeZone === '' || /^[+-]/.test(timeZone)) {
        throw new IsoWeekError('INVALID_TIME_ZONE', `Keine IANA-Zeitzone: ${String(timeZone)}`);
    }
    let formatter;
    try {
        formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            era: 'short',
        });
    }
    catch {
        throw new IsoWeekError('INVALID_TIME_ZONE', `Keine IANA-Zeitzone: ${timeZone}`);
    }
    const parts = formatter.formatToParts(instant);
    const part = (type) => parts.find((candidate) => candidate.type === type)?.value ?? '';
    // Vor Christus liefert `year` den Betrag; das Zeitalter schließt das aus.
    if (part('era') !== 'AD') {
        throw new IsoWeekError('INVALID_DATE', 'Zeitpunkt außerhalb 0001 … 9999');
    }
    const date = `${part('year').padStart(4, '0')}-${part('month')}-${part('day')}`;
    parseCalendarDate(date);
    return date;
}
function isoWeekOf(date) {
    return weekOfDayNumber(parseCalendarDate(date));
}
function currentIsoWeek(now, timeZone) {
    return isoWeekOf(calendarDateIn(now, timeZone));
}
function mondayOf(week) {
    return formatDayNumber(mondayDayNumber(week));
}
function addWeeks(week, n) {
    if (!Number.isInteger(n)) {
        throw new IsoWeekError('OUT_OF_RANGE', `Wochenanzahl ist nicht ganzzahlig: ${String(n)}`);
    }
    return weekOfDayNumber(REFERENCE_MONDAY + (weekIndex(week) + n) * 7);
}
/** `to` minus `from` in Wochen. */
function weeksBetween(from, to) {
    return weekIndex(to) - weekIndex(from);
}
function compareIsoWeeks(a, b) {
    return Math.sign(weeksBetween(b, a));
}
function formatIsoWeekKey(week) {
    assertValidWeek(week);
    return `${String(week.isoYear).padStart(4, '0')}-W${String(week.isoWeek).padStart(2, '0')}`;
}
const KEY_FORM = /^(\d{4})-?W(\d{1,2})$/i;
const KW_FORM = /^KW\s*(\d{1,2})\s*\/\s*(\d{4})$/i;
/**
 * Akzeptiert ohne Rücksicht auf Groß-/Kleinschreibung und Leerraum außen:
 * `YYYY-Www`, `YYYY-Ww`, `YYYYWww`, `KWw/YYYY`, `KW ww / YYYY`.
 */
function parseIsoWeek(input) {
    const text = typeof input === 'string' ? input.trim() : '';
    const keyMatch = KEY_FORM.exec(text);
    const kwMatch = keyMatch ? null : KW_FORM.exec(text);
    let week;
    if (keyMatch)
        week = { isoYear: Number(keyMatch[1]), isoWeek: Number(keyMatch[2]) };
    else if (kwMatch)
        week = { isoYear: Number(kwMatch[2]), isoWeek: Number(kwMatch[1]) };
    else
        throw new IsoWeekError('UNPARSEABLE', `Keine Kalenderwoche erkannt: ${String(input)}`);
    assertValidWeek(week);
    return week;
}
function toPlanningWeek(week) {
    return {
        isoYear: week.isoYear,
        isoWeek: week.isoWeek,
        key: formatIsoWeekKey(week),
        monday: mondayOf(week),
    };
}
function horizonWeekCount(start, length) {
    if ('years' in length) {
        const { years } = length;
        if (!Number.isInteger(years) || years < 1 || years > exports.MAX_HORIZON_YEARS) {
            throw new IsoWeekError('HORIZON_OUT_OF_RANGE', `Jahre außerhalb 1 … ${exports.MAX_HORIZON_YEARS}: ${String(years)}`);
        }
        const targetYear = start.isoYear + years;
        if (targetYear > MAX_YEAR)
            throw new IsoWeekError('OUT_OF_RANGE', 'Horizont endet nach 9999');
        // Endregel E5: gleicher Schlüssel `years` später, sonst KW 1 des Folgejahres.
        const endIndex = start.isoWeek <= weeksInIsoYear(targetYear)
            ? weekIndex({ isoYear: targetYear, isoWeek: start.isoWeek })
            : weekIndex({ isoYear: targetYear, isoWeek: 1 }) + weeksInIsoYear(targetYear);
        return endIndex - weekIndex(start);
    }
    const { weeks } = length;
    if (!Number.isInteger(weeks) || weeks < 1 || weeks > exports.MAX_HORIZON_WEEKS) {
        throw new IsoWeekError('HORIZON_OUT_OF_RANGE', `Wochen außerhalb 1 … ${exports.MAX_HORIZON_WEEKS}: ${String(weeks)}`);
    }
    return weeks;
}
/**
 * Lückenlose Liste der Kalenderwochen ab `start` (E5). Nur Kalender: wie viele
 * Wochen Daten geladen werden dürfen, regelt ADR-07 (höchstens 26), nicht diese
 * Funktion.
 */
function planningHorizon(start, length = exports.DEFAULT_HORIZON) {
    assertValidWeek(start);
    const count = horizonWeekCount(start, length);
    const first = weekIndex(start);
    const weeks = [];
    for (let offset = 0; offset < count; offset += 1) {
        weeks.push(toPlanningWeek(weekOfDayNumber(REFERENCE_MONDAY + (first + offset) * 7)));
    }
    return weeks;
}
/** Objektform mit Bereichsprüfung, z. B. für tRPC-Eingaben. */
exports.isoWeekObjectSchema = zod_1.z
    .strictObject({
    isoYear: zod_1.z.number().int().min(MIN_YEAR).max(MAX_YEAR),
    isoWeek: zod_1.z.number().int().min(1).max(53),
})
    .refine(isValidIsoWeek, { message: 'Diese Kalenderwoche gibt es nicht', path: ['isoWeek'] });
/** Nimmt jede Schreibweise von `parseIsoWeek` und liefert die Objektform. */
exports.isoWeekInputSchema = zod_1.z.string().transform((value, ctx) => {
    try {
        return parseIsoWeek(value);
    }
    catch (error) {
        if (!(error instanceof IsoWeekError))
            throw error;
        ctx.addIssue({ code: 'custom', message: error.message });
        return zod_1.z.NEVER;
    }
});
//# sourceMappingURL=iso-week.js.map